import { useEffect, useState } from 'react';
import { CANARY_EVENT } from './canary';

/**
 * SEC.3 — Renders the "Gotcha" full-screen overlay when an attacker
 * exploring DevTools / network tab triggers any honeypot decoy endpoint
 * AND the legitimate frontend renders the response (which Layer 1 already
 * captured server-side; this layer adds the visual lock + signals back
 * to the attacker that they've been logged).
 *
 * The modal cannot be dismissed by `Escape` and is rendered as a top-level
 * fixed overlay with z-index above every other DeepSync surface. The
 * "Acknowledge" button only clears the local modal state — the server-side
 * notification has already fired by the time this renders.
 */
interface CanaryEventDetail {
  decoyId: string;
  serverMeta: Record<string, unknown>;
}

export function HoneypotGotchaModal() {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<CanaryEventDetail | null>(null);

  useEffect(() => {
    const handler = (ev: Event) => {
      const customEv = ev as CustomEvent<CanaryEventDetail>;
      setDetail(customEv.detail);
      setOpen(true);
    };
    window.addEventListener(CANARY_EVENT, handler);
    return () => window.removeEventListener(CANARY_EVENT, handler);
  }, []);

  if (!open) return null;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(0, 0, 0, 0.92)',
    zIndex: 2147483647,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    backdropFilter: 'blur(8px)',
  };

  const cardStyle: React.CSSProperties = {
    maxWidth: 560,
    padding: '32px 40px',
    border: '2px solid #ef4444',
    borderRadius: 12,
    background: 'rgba(20, 20, 20, 0.95)',
    boxShadow: '0 0 60px rgba(239, 68, 68, 0.4)',
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Honeypot triggered" style={overlayStyle}>
      <div style={cardStyle}>
        <div style={{ fontSize: 48, lineHeight: 1, marginBottom: 16 }}>🍯</div>
        <h1 style={{ margin: 0, fontSize: 32, fontWeight: 700, color: '#ef4444' }}>
          Gotcha.
        </h1>
        <p style={{ marginTop: 16, fontSize: 16, lineHeight: 1.55, color: '#e5e7eb' }}>
          You triggered a DeepSync honeypot endpoint. Your IP address, browser
          fingerprint, full request headers, and the timestamp of this event
          have been captured and forwarded to the DeepSync security team.
        </p>
        {detail?.decoyId && (
          <p style={{ marginTop: 12, fontSize: 13, color: '#9ca3af', fontFamily: 'ui-monospace, monospace' }}>
            decoy: <span style={{ color: '#fbbf24' }}>{detail.decoyId}</span>
          </p>
        )}
        <p style={{ marginTop: 16, fontSize: 13, color: '#9ca3af', lineHeight: 1.55 }}>
          This endpoint exists only to detect unauthorized access attempts. No
          legitimate DeepSync user reaches it. If you arrived here in error,
          please contact <span style={{ color: '#60a5fa' }}>security@deep-sync.io</span> with this decoy ID.
        </p>
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            marginTop: 24,
            padding: '10px 20px',
            background: '#1f2937',
            color: '#e5e7eb',
            border: '1px solid #374151',
            borderRadius: 6,
            fontSize: 14,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Acknowledge
        </button>
      </div>
    </div>
  );
}
